<template>
  <div>
    <h3>
      {{ t('linkAgency') }}
    </h3>
    <SearchUserInput
      :allowed-roles="[UserRole.AGENCY, UserRole.COMPANY]"
      class="max-w-xl"
      :selected-user="selectedAgency"
      :placeholder="t('agency')"
      @update-user="handleUpdateAgency"
    />
    <Divider />
    <div class="grid flex-row-reverse gap-2 md:flex md:items-end md:justify-between">
      <div class="grid items-end gap-4 md:flex">
        <TotalPrice :total-price="totalPrice" />
        <CVButton variant="default" :disabled="!selectedAgency" @click="linkAgent">{{
          t('linkAgency')
        }}</CVButton>
      </div>
      <CVButton variant="link" @click="$emit('back')">{{ t('back') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import { useI18n } from 'vue-i18n';
import { ref, watchEffect } from 'vue';
import TotalPrice from '../../TotalPrice.vue';
import type { User } from '@/entities/auth/user.entity';
import SearchUserInput from '@/components/booking-forms/customer/SearchUserInput.vue';
import { Alert } from '@/services/alert.service';
import { isCustomerPaidPartner } from '@/utils/long-term-create.utils';

const props = defineProps<{
  agency: User | null;
  totalPrice: number;
  isLongTerm?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:agency', value: User): void;
  (e: 'continue'): void;
  (e: 'back'): void;
}>();

const { t } = useI18n();

const selectedAgency = ref<User | undefined>(props.agency ?? undefined);
watchEffect(() => {
  selectedAgency.value = props.agency ?? undefined;
});
const handleUpdateAgency = (user: User | undefined) => {
  selectedAgency.value = user ?? undefined;
};

const linkAgent = async () => {
  if (!selectedAgency.value) {
    return;
  }
  if (props.isLongTerm && !isCustomerPaidPartner(selectedAgency.value)) {
    await Alert.fire({
      text: t('longTermCustomerPaidAgency'),
      icon: 'error',
    });
    return;
  }
  emit('update:agency', selectedAgency.value);
  emit('continue');
};
</script>

<i18n lang="json">
{
  "en": {
    "linkAgency": "Link Agency",
    "agency": "Agency",
    "longTermCustomerPaidAgency": "Long-term rental can only be booked with a customer-paid agency."
  },
  "de": {
    "linkAgency": "Vermittler verknüpfen",
    "agency": "Vermittler",
    "longTermCustomerPaidAgency": "Langzeitmiete kann nur mit einer kundenseitig abgerechneten Agency gebucht werden."
  },
  "es": {
    "linkAgency": "Vincular agencia",
    "agency": "Agencia",
    "longTermCustomerPaidAgency": "El alquiler a largo plazo solo se puede reservar con una agencia de cobro al cliente."
  }
}
</i18n>
